import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import UserMenu from './UserMenu';
import './AuthManager.css';

/**
 * AuthManager - Componente que maneja toda la lógica de autenticación UI
 * Controla los modales de login, signup y el menú de usuario
 */
const AuthManager = ({ className = '', onMenuAction }) => {
  const { isAuthenticated, userDisplayName } = useAuth();
  
  // Estados para controlar qué modal está abierto
  const [activeModal, setActiveModal] = useState(null); // 'login', 'signup', null
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Funciones para manejar la apertura/cierre de modales
  const openLoginModal = () => {
    setActiveModal('login');
    setIsUserMenuOpen(false);
    onMenuAction?.(); // Cerrar menú hamburguesa si existe
  };

  const openSignupModal = () => {
    setActiveModal('signup');
    setIsUserMenuOpen(false);
    onMenuAction?.(); // Cerrar menú hamburguesa si existe
  };

  const closeModals = () => {
    setActiveModal(null);
  };

  const switchToSignup = () => {
    setActiveModal('signup');
  };

  const switchToLogin = () => {
    setActiveModal('login');
  };

  // Funciones para el menú de usuario
  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setActiveModal(null);
    if (!isUserMenuOpen) {
      onMenuAction?.(); // Cerrar menú hamburguesa al abrir user menu
    }
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  // Cerrar todo al hacer clic fuera
  const handleOverlayClick = () => {
    closeModals();
    closeUserMenu();
  };

  if (!isAuthenticated) {
    // Usuario no autenticado - mostrar botones de login/registro
    return (
      <div className={`auth-manager ${className}`}>
        <div className="auth-buttons">
          <button 
            className="auth-btn auth-btn-login"
            onClick={openLoginModal}
          >
            Iniciar Sesión
          </button>
          <button 
            className="auth-btn auth-btn-signup"
            onClick={openSignupModal}
          >
            Registrarse
          </button>
        </div>

        {/* Modales de autenticación */}
        <LoginModal
          isOpen={activeModal === 'login'}
          onClose={closeModals}
          onSwitchToSignup={switchToSignup}
        />
        
        <SignupModal
          isOpen={activeModal === 'signup'}
          onClose={closeModals}
          onSwitchToLogin={switchToLogin}
        />
      </div>
    );
  }

  // Usuario autenticado - mostrar información del usuario
  return (
    <div className={`auth-manager authenticated ${className}`}>
      <div className="user-info">
        <button 
          className="user-profile-btn"
          onClick={toggleUserMenu}
          aria-label="Abrir menú de usuario"
          aria-expanded={isUserMenuOpen}
        >
          <div className="user-avatar">
            {userDisplayName?.charAt(0)?.toUpperCase() || '👤'}
          </div>
          <span className="user-name">{userDisplayName}</span>
          <span className="dropdown-arrow">
            {isUserMenuOpen ? '▲' : '▼'}
          </span>
        </button>
      </div>

      {/* Menú de usuario */}
      <UserMenu
        isOpen={isUserMenuOpen}
        onClose={closeUserMenu}
      />

      {/* Overlay para cerrar menú al hacer clic fuera */}
      {isUserMenuOpen && (
        <div 
          className="auth-overlay"
          onClick={handleOverlayClick}
        />
      )}
    </div>
  );
};

export default AuthManager;
